import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
} from "@mui/material";
import {
  FiberManualRecord,
  AccessTime,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";
import { motion } from "framer-motion";

const NEWS_ARTICLES = [
  {
    id: 1,
    title: "Kerala Entrepreneurs to Launch Farm Robot in US",
    description: "Autogenbot's Automist 500X, developed in Kerala with government support, uses precision spraying to cut farm costs and will be launched globally at GOFAR 2025 in California",
    image: "/images/launch-img.jpeg",
    category: "Launch",
    date: "21 September 2025",
    readTime: "1 min read",
    link: "https://en.channeliam.com/2025/09/21/automist-500x-farm-robot-launch/",
  },
  {
    id: 2,
    title: "#FIRAUSA25 | FIRA - World Ag Robotics Forum",
    description: "Beyond the live demos, even more groundbreaking robots and agtech solutions are taking the stage at the expo, the smart tech pitches or during networking times at FIRA USA 2025",
    image: "/images/fira-img.jpg",
    category: "Growth",
    date: "23 October 2025",
    readTime: "1 min read",
    link: "https://www.linkedin.com/feed/update/urn:li:activity:7382429784741662720/?actorCompanyId=89851894",
  },
  {
    id: 3,
    title: "On the Frontier of Robotics - Frontier Tech Hub ",
    description: "The tipping point has arrived. It's time to shift the momentum to where it's most needed.",
    image: "/images/frontier-img.png",
    category: "Technology",
    date: "12 March 2025",
    readTime: "3 min read",
    link: "https://www.frontiertechhub.org/insights/otf-robotics",
  },
 
];

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  background: "linear-gradient(180deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  paddingTop: "40px",
  paddingBottom: "40px",
  [theme.breakpoints.down('md')]: {
    paddingTop: "30px",
    paddingBottom: "30px",
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: "24px",
    paddingBottom: "24px",
  },
}));

const GradientOrb = styled(Box)(({ top, left, bottom, right, color }) => ({
  position: "absolute",
  width: 500,
  height: 500,
  borderRadius: "50%",
  background: color,
  filter: "blur(100px)",
  pointerEvents: "none",
  top,
  left,
  bottom,
  right,
}));

const GridPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.05,
  backgroundImage: `linear-gradient(rgba(148, 163, 184, 0.1) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px)`,
  backgroundSize: "50px 50px",
  pointerEvents: "none",
});

const NewsCard = styled(Card)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(20px)",
  borderRadius: 20,
  [theme.breakpoints.down('sm')]: {
    borderRadius: 16,
  },
  border: "1px solid rgba(193, 227, 40, 0.3)",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  transition: "all 0.3s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(193, 227, 40, 0.05) 0%, transparent 50%, rgba(193, 227, 40, 0.05) 100%)",
    opacity: 0,
    transition: "opacity 0.3s ease",
    zIndex: 1,
  },
  "&:hover": {
    borderColor: "rgba(193, 227, 40, 0.5)",
    transform: "translateY(-8px)",
    "&::before": {
      opacity: 1,
    },
    "& .news-image": {
      transform: "scale(1.1)",
    },
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 220,
  [theme.breakpoints.down('sm')]: {
    height: 180,
  },
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "50%",
    background: "linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, transparent 100%)",
  },
}));

const CategoryChip = styled(Chip)(({ category, theme }) => {
  const colors = {
    Launch: { bg: "rgba(239, 68, 68, 0.15)", text: "#ef4444", border: "rgba(239, 68, 68, 0.3)" },
    Awards: { bg: "rgba(251, 191, 36, 0.15)", text: "#fbbf24", border: "rgba(251, 191, 36, 0.3)" },
    Growth: { bg: "rgba(193, 227, 40, 0.15)", text: "#c1e328", border: "rgba(193, 227, 40, 0.3)" },
    Technology: { bg: "rgba(59, 130, 246, 0.15)", text: "#3b82f6", border: "rgba(59, 130, 246, 0.3)" },
    Sustainability: { bg: "rgba(34, 197, 94, 0.15)", text: "#22c55e", border: "rgba(34, 197, 94, 0.3)" },
    Innovation: { bg: "rgba(168, 85, 247, 0.15)", text: "#a855f7", border: "rgba(168, 85, 247, 0.3)" },
    Business: { bg: "rgba(236, 72, 153, 0.15)", text: "#ec4899", border: "rgba(236, 72, 153, 0.3)" },
  };

  const color = colors[category] || colors.Technology;

  return {
    background: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`,
    fontWeight: 700,
    fontSize: "0.625rem",
    [theme.breakpoints.down('sm')]: {
      fontSize: "0.5rem",
      height: 20,
    },
    height: 24,
    letterSpacing: 0.5,
  };
});

const NewsCardComponent = ({ article }) => {
  return (
    <NewsCard elevation={0}>
      <StyledCardMedia
        className="news-image-wrapper"
        component="div"
      >
        <Box
          component="img"
          src={article.image}
          alt={article.title}
          className="news-image"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s ease",
          }}
        />
        
        {/* Category Badge Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: { xs: 12, sm: 16 },
            left: { xs: 12, sm: 16 },
            zIndex: 2,
          }}
        >
          <CategoryChip
            label={article.category.toUpperCase()}
            category={article.category}
            size="small"
          />
        </Box>
      </StyledCardMedia>

      <CardContent sx={{ p: { xs: 2, sm: 3 }, position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Date and Read Time */}
        <Stack
          direction="row"
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems="center"
          sx={{ mb: { xs: 1.5, sm: 2 } }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTime sx={{ fontSize: { xs: 12, sm: 14 }, color: "rgba(148, 163, 184, 1)" }} />
            <Typography
              variant="caption"
              sx={{ color: "rgba(148, 163, 184, 1)", fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
            >
              {article.date}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            sx={{ color: "rgba(148, 163, 184, 1)", fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
          >
            {article.readTime}
          </Typography>
        </Stack>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            color: "#fff",
            fontWeight: 700,
            mb: { xs: 1, sm: 1.5 },
            fontSize: { xs: "1rem", sm: "1.1rem" },
            lineHeight: 1.4,
          }}
        >
          {article.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: "rgba(203, 213, 225, 1)",
            lineHeight: 1.6,
            fontSize: { xs: "0.8rem", sm: "0.85rem" },
            flex: 1,
          }}
        >
          {article.description}
        </Typography>

        {/* Read More Link */}
        <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
          <Typography
            component="a"
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            variant="button"
            sx={{
              color: "#c1e328",
              fontWeight: 700,
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              letterSpacing: 1,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              textDecoration: "none",
              transition: "gap 0.3s ease",
              "&:hover": {
                gap: 1,
              },
            }}
          >
            READ MORE →
          </Typography>
        </Box>
      </CardContent>
    </NewsCard>
  );
};

const HomepageNews = () => {
  // Create a long array for seamless infinite scroll
  const infiniteNews = [...NEWS_ARTICLES, ...NEWS_ARTICLES, ...NEWS_ARTICLES, ...NEWS_ARTICLES];

  return (
    <BackgroundBox>
      {/* Background Elements */}
      <GradientOrb top="33%" left={-256} color="rgba(193, 227, 40, 0.1)" />
      <GradientOrb bottom="33%" right={-256} color="rgba(193, 227, 40, 0.1)" />
      <GridPattern />

      <Container maxWidth="lg" sx={{ position: "relative", px: { xs: 2, sm: 3 } }}>
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 4, sm: 6, md: 8 }}>
          {/* Badge */}
          <Chip
            icon={
              <FiberManualRecord
                sx={{
                  fontSize: 8,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
            }
            label="LATEST UPDATES"
            sx={{
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(193, 227, 40, 0.3)",
              color: "#c1e328",
              fontWeight: 700,
              letterSpacing: { xs: 2, sm: 3.2 },
              fontSize: { xs: "0.5rem", sm: "0.555rem" },
              mb: { xs: 2, sm: 3 },
            }}
          />

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              fontWeight: 700,
              color: "#fff",
              letterSpacing: -1,
              mb: 2,
            }}
          >
            News &{" "}
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(135deg, #c1e328 0%, #b5d028 50%, #a8c728 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Insights
            </Box>
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: "rgba(148, 163, 184, 1)",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}
          >
            Stay updated with the latest innovations, achievements, and insights
            from the world of autonomous agriculture
          </Typography>
        </Box>
      </Container>

      {/* News Carousel - Full Width - Continuous Scroll */}
      <Box sx={{ mb: { xs: 4, sm: 6 }, overflow: "hidden", position: "relative", width: "100%", paddingY: { xs: 1, sm: 2 } }}>
        <motion.div
          style={{
            display: "flex",
            gap: "24px",
            paddingLeft: "24px",
          }}
          animate={{
            x: [0, -100 * NEWS_ARTICLES.length + "%"],
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: 200, // Adjust speed (higher = slower)
              ease: "linear",
            },
          }}
          whileHover={{
            animationPlayState: "paused",
          }}
        >
          {infiniteNews.map((article, index) => (
            <Box
              key={`${article.id}-${index}`}
              sx={{
                minWidth: { xs: "85vw", sm: "45vw", md: "32vw", lg: "28vw" },
                maxWidth: { xs: "85vw", sm: "45vw", md: "32vw", lg: "28vw" },
                flexShrink: 0,
              }}
            >
              <NewsCardComponent article={article} />
            </Box>
          ))}
        </motion.div>

        {/* Gradient Overlays for fade effect */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "100px",
            background: "linear-gradient(to right, #000000, transparent)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "100px",
            background: "linear-gradient(to left, #000000, transparent)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      </Box>
    </BackgroundBox>
  );
};

export default HomepageNews;

